import { CircuitResponse } from '@/types/circuits/CircuitResponse';
import { CURRENCIES } from '../../../../shared/CountriesData';
import {
  createCircuitDay,
  createEmptyCircuitDays,
} from './createEmptyCircuitDays';
import { CircuitFormData } from '@/types/circuits/CircuitFormData';
import { getBenefitTypeName } from '../benefits/getBenefitTypeName';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { CIRCUIT_DAY_PERIODS } from '@/constants/circuitDayPeriods';
import { Translator } from '@/types/translator.type';
import { SupportedLocale } from '@/types/supported-locale.type';
import i18n, { LOCALES } from '@/i18n/config';
import { numberIfNotUndefined } from '../prices/numberIfNotUndefined';
import { type BenefitTranslation } from '@/i18n/translation-type-utils';

type BenefitTranslationMap = Map<
  string,
  Record<SupportedLocale, BenefitTranslation>
>;

const createBenefitTranslationMap = (
  circuit: CircuitResponse,
): BenefitTranslationMap => {
  const translationMap = new Map<
    string,
    Record<SupportedLocale, BenefitTranslation>
  >();

  circuit.circuitDays.forEach((day, dayIndex) => {
    day.CircuitDayStep.forEach((step, stepIndex) => {
      // Process standard benefit
      if (step.standardBenefit) {
        const benefitId = step.standardBenefit.id;
        const translations: Record<SupportedLocale, BenefitTranslation> =
          {} as Record<SupportedLocale, BenefitTranslation>;

        LOCALES.forEach((locale) => {
          const dayTranslations =
            circuit.translations?.[locale]?.circuitDays?.[dayIndex];
          const stepTranslations = dayTranslations?.CircuitDayStep?.[stepIndex];
          const benefitTranslation = stepTranslations?.standardBenefit;

          translations[locale] = {
            name: benefitTranslation?.name || step.standardBenefit?.name,
            activity: benefitTranslation?.activity
              ? {
                  shortDescription:
                    benefitTranslation.activity.shortDescription,
                  longDescription: benefitTranslation.activity.longDescription,
                }
              : undefined,
            flight: benefitTranslation?.flight
              ? {
                  shortDescription: benefitTranslation.flight.shortDescription,
                  longDescription: benefitTranslation.flight.longDescription,
                }
              : undefined,
            VehicleWithChauffeur: benefitTranslation?.VehicleWithChauffeur
              ? {
                  shortDescription:
                    benefitTranslation.VehicleWithChauffeur.shortDescription,
                  longDescription:
                    benefitTranslation.VehicleWithChauffeur.longDescription,
                }
              : undefined,
            hotel: benefitTranslation?.hotel
              ? {
                  shortDescriptionBody:
                    benefitTranslation.hotel.shortDescriptionBody,
                  longDescriptionBody:
                    benefitTranslation.hotel.longDescriptionBody,
                  HotelRoomCategory: benefitTranslation.hotel.HotelRoomCategory
                    ? benefitTranslation.hotel.HotelRoomCategory.map(
                        (category) => ({
                          name: category?.name ?? undefined,
                          shortDescription:
                            category?.shortDescription ?? undefined,
                          longDescription:
                            category?.longDescription ?? undefined,
                        }),
                      )
                    : undefined,
                }
              : undefined,
            carRental: benefitTranslation?.carRental
              ? {
                  shortDescription:
                    benefitTranslation.carRental.shortDescription,
                  longDescription: benefitTranslation.carRental.longDescription,
                  insurances: benefitTranslation.carRental.insurances?.map(
                    (insurance) => ({
                      name: insurance?.name ?? undefined,
                      shortDescription:
                        insurance?.shortDescription ?? undefined,
                      longDescription: insurance?.longDescription ?? undefined,
                    }),
                  ),
                  taxes: benefitTranslation.carRental.taxes?.map((tax) => ({
                    name: tax?.name ?? undefined,
                  })),
                  options: benefitTranslation.carRental.options?.map(
                    (option) => ({
                      name: option?.name ?? undefined,
                      description: option?.description ?? undefined,
                    }),
                  ),
                  oneWayFees: benefitTranslation.carRental.oneWayFees?.map(
                    (fee) => ({
                      name: fee?.name ?? undefined,
                      description: fee?.description ?? undefined,
                    }),
                  ),
                  carCategories: benefitTranslation.carRental.carCategories
                    ? benefitTranslation.carRental.carCategories.map(
                        (category) => ({
                          category: category?.category ?? undefined,
                          model: category?.model ?? undefined,
                          description: category?.description ?? undefined,
                          included: category?.included ?? undefined,
                          notIncluded: category?.notIncluded ?? undefined,
                          insurances: category?.insurances?.map(
                            (insurance) => ({
                              name: insurance?.name ?? undefined,
                              shortDescription:
                                insurance?.shortDescription ?? undefined,
                              longDescription:
                                insurance?.longDescription ?? undefined,
                            }),
                          ),
                          taxes: category?.taxes?.map((tax) => ({
                            name: tax?.name ?? undefined,
                          })),
                          options: category?.options?.map((option) => ({
                            name: option?.name ?? undefined,
                            description: option?.description ?? undefined,
                          })),
                          oneWayFees: category?.oneWayFees?.map((fee) => ({
                            name: fee?.name ?? undefined,
                            description: fee?.description ?? undefined,
                          })),
                        }),
                      )
                    : undefined,
                }
              : undefined,
          };
        });

        translationMap.set(benefitId, translations);
      }

      // Process optional benefits
      step.optionalBenefits.forEach((benefit, optionalIndex) => {
        const benefitId = benefit.id;
        const translations: Record<SupportedLocale, BenefitTranslation> =
          {} as Record<SupportedLocale, BenefitTranslation>;

        LOCALES.forEach((locale) => {
          const dayTranslations =
            circuit.translations?.[locale]?.circuitDays?.[dayIndex];
          const stepTranslations = dayTranslations?.CircuitDayStep?.[stepIndex];
          const benefitTranslation =
            stepTranslations?.optionalBenefits?.[optionalIndex];

          translations[locale] = {
            name: benefitTranslation?.name || benefit.name,
            activity: benefitTranslation?.activity
              ? {
                  shortDescription:
                    benefitTranslation.activity.shortDescription,
                  longDescription: benefitTranslation.activity.longDescription,
                }
              : undefined,
            flight: benefitTranslation?.flight
              ? {
                  shortDescription: benefitTranslation.flight.shortDescription,
                  longDescription: benefitTranslation.flight.longDescription,
                }
              : undefined,
            VehicleWithChauffeur: benefitTranslation?.VehicleWithChauffeur
              ? {
                  shortDescription:
                    benefitTranslation.VehicleWithChauffeur.shortDescription,
                  longDescription:
                    benefitTranslation.VehicleWithChauffeur.longDescription,
                }
              : undefined,
            hotel: benefitTranslation?.hotel
              ? {
                  shortDescriptionBody:
                    benefitTranslation.hotel.shortDescriptionBody,
                  longDescriptionBody:
                    benefitTranslation.hotel.longDescriptionBody,
                  HotelRoomCategory: benefitTranslation.hotel.HotelRoomCategory
                    ? benefitTranslation.hotel.HotelRoomCategory.map(
                        (category) => ({
                          name: category?.name ?? undefined,
                          shortDescription:
                            category?.shortDescription ?? undefined,
                          longDescription:
                            category?.longDescription ?? undefined,
                        }),
                      )
                    : undefined,
                }
              : undefined,
            carRental: benefitTranslation?.carRental
              ? {
                  shortDescription:
                    benefitTranslation.carRental.shortDescription,
                  longDescription: benefitTranslation.carRental.longDescription,
                  insurances: benefitTranslation.carRental.insurances?.map(
                    (insurance) => ({
                      name: insurance?.name ?? undefined,
                      shortDescription:
                        insurance?.shortDescription ?? undefined,
                      longDescription: insurance?.longDescription ?? undefined,
                    }),
                  ),
                  taxes: benefitTranslation.carRental.taxes?.map((tax) => ({
                    name: tax?.name ?? undefined,
                  })),
                  options: benefitTranslation.carRental.options?.map(
                    (option) => ({
                      name: option?.name ?? undefined,
                      description: option?.description ?? undefined,
                    }),
                  ),
                  oneWayFees: benefitTranslation.carRental.oneWayFees?.map(
                    (fee) => ({
                      name: fee?.name ?? undefined,
                      description: fee?.description ?? undefined,
                    }),
                  ),
                  carCategories: benefitTranslation.carRental.carCategories
                    ? benefitTranslation.carRental.carCategories.map(
                        (category) => ({
                          category: category?.category ?? undefined,
                          model: category?.model ?? undefined,
                          description: category?.description ?? undefined,
                          included: category?.included ?? undefined,
                          notIncluded: category?.notIncluded ?? undefined,
                          insurances: category?.insurances?.map(
                            (insurance) => ({
                              name: insurance?.name ?? undefined,
                              shortDescription:
                                insurance?.shortDescription ?? undefined,
                              longDescription:
                                insurance?.longDescription ?? undefined,
                            }),
                          ),
                          taxes: category?.taxes?.map((tax) => ({
                            name: tax?.name ?? undefined,
                          })),
                          options: category?.options?.map((option) => ({
                            name: option?.name ?? undefined,
                            description: option?.description ?? undefined,
                          })),
                          oneWayFees: category?.oneWayFees?.map((fee) => ({
                            name: fee?.name ?? undefined,
                            description: fee?.description ?? undefined,
                          })),
                        }),
                      )
                    : undefined,
                }
              : undefined,
          };
        });

        translationMap.set(benefitId, translations);
      });
    });
  });

  return translationMap;
};

const initializeCircuitTranslations = (circuit: CircuitResponse) => {
  const translations = {} as Record<
    SupportedLocale,
    {
      name?: string;
      summary?: string;
      importantPoints?: string;
      priceIncludes?: string;
      priceExcludes?: string;
      modificationConditions?: string;
      cancellationConditions?: string;
      circuitDays?: {
        stepTitle?: string;
        description?: string;
      }[];
      circuitAdditionalParagraphs?: {
        title?: string;
        content?: string;
      }[];
    }
  >;

  for (const locale of LOCALES) {
    translations[locale] = {
      name: circuit.translations?.[locale]?.name || circuit.name || undefined,
      summary:
        circuit.translations?.[locale]?.summary || circuit.summary || undefined,
      importantPoints:
        circuit.translations?.[locale]?.importantPoints ||
        circuit.importantPoints ||
        undefined,
      priceIncludes:
        circuit.translations?.[locale]?.priceIncludes ||
        circuit.priceIncludes ||
        undefined,
      priceExcludes:
        circuit.translations?.[locale]?.priceExcludes ||
        circuit.priceExcludes ||
        undefined,
      modificationConditions:
        circuit.translations?.[locale]?.modificationConditions ||
        circuit.modificationConditions ||
        undefined,
      cancellationConditions:
        circuit.translations?.[locale]?.cancellationConditions ||
        circuit.cancellationConditions ||
        undefined,
      circuitDays:
        circuit.translations?.[locale]?.circuitDays ||
        circuit.circuitDays ||
        undefined,
    };
  }

  return {
    ...circuit.translations,
  };
};

export const loadCircuitDefaultValues = ({
  circuit,
  t,
  l,
}: {
  circuit: CircuitResponse;
  t: Translator;
  l?: SupportedLocale;
}) => {
  const translationMap = createBenefitTranslationMap(circuit);
  const translations = initializeCircuitTranslations(circuit);
  const translation = translations[l ?? (i18n.language as SupportedLocale)];

  return {
    translations,
    name: circuit.name ?? undefined,
    remarks: circuit.remarks ?? undefined,
    countries:
      circuit.countries && circuit.currencies && circuit.countries.length
        ? circuit.countries.map((country, index) => ({
            country: {
              label: country,
              value: country,
            },
            currency: {
              label: CURRENCIES.find(
                (currency) => currency.value === circuit.currencies[index],
              )?.label,
              value: circuit.currencies[index],
            },
          }))
        : [
            {
              country: undefined,
              currency: undefined,
            },
          ],
    marginRate: numberIfNotUndefined(circuit.marginRate),
    nbDays: circuit.durationDays ? Number(circuit.durationDays) : undefined,
    nbNights: circuit.durationNights
      ? Number(circuit.durationNights)
      : undefined,
    nbParticipants: circuit.nbParticipants
      ? Number(circuit.nbParticipants)
      : undefined,
    // Legacy backend contract (circuit flow): nbAdults stores adolescents.
    nbAdolescents: circuit.nbAdults ? Number(circuit.nbAdults) : 0,
    nbChildren: circuit.nbChildren ? Number(circuit.nbChildren) : 0,
    childAges:
      circuit.secondChildAgeFrom && circuit.secondChildAgeTo
        ? [
            {
              childAgeFrom: circuit.childAgeFrom ?? 2,
              childAgeTo: circuit.childAgeTo ?? 11,
            },
            {
              childAgeFrom: circuit.secondChildAgeFrom ?? 12,
              childAgeTo: circuit.secondChildAgeTo ?? 17,
            },
          ]
        : [
            {
              childAgeFrom: circuit.childAgeFrom ?? 2,
              childAgeTo: circuit.childAgeTo ?? 11,
            },
          ],
    mainImage: circuit.mainImage ?? undefined,
    images: circuit.images ?? [],
    summary: (circuit.summary || translation?.summary) ?? undefined,
    importantPoints:
      (circuit.importantPoints || translation?.importantPoints) ?? undefined,
    priceIncludes:
      (circuit.priceIncludes || translation?.priceIncludes) ?? undefined,
    priceExcludes:
      (circuit.priceExcludes || translation?.priceExcludes) ?? undefined,
    modificationsConditions:
      (circuit.modificationConditions || translation?.modificationConditions) ??
      undefined,
    cancellationConditions:
      (circuit.cancellationConditions || translation?.cancellationConditions) ??
      undefined,
    additionalParagraphs: (circuit.circuitAdditionalParagraphs ?? []).map(
      (paragraph) => ({
        ...paragraph,
        // A paragraph saved with a title only comes back with null content.
        content: paragraph.content ?? undefined,
      }),
    ),
    circuitDays:
      circuit.circuitDays && circuit.circuitDays.length
        ? addMultiDaySteps(circuit.circuitDays).map((circuitDay, indexDay) => ({
            stepTitle:
              (circuitDay.stepTitle ||
                translation?.circuitDays?.[indexDay]?.stepTitle) ??
              undefined,
            description:
              (circuitDay.description ||
                translation?.circuitDays?.[indexDay]?.description) ??
              undefined,
            images: circuitDay.images ?? [],
            steps:
              circuitDay.CircuitDayStep && circuitDay.CircuitDayStep.length
                ? parseSteps(circuitDay.CircuitDayStep, translationMap, t)
                : [],
          }))
        : createEmptyCircuitDays(1),
  } as const;
};

export const parseSteps = (
  steps: CircuitResponse['circuitDays'][number]['CircuitDayStep'],
  translationMap: BenefitTranslationMap | undefined,
  t: Translator,
) => {
  const circuitDaySteps: CircuitFormData['circuitDays'][number]['steps'] = [
    createCircuitDay().steps[0],
  ];
  let [indexTransport, indexActivity, indexMeal, indexAccommodation] = [
    0, 0, 0, 0,
  ];
  steps.forEach((step) => {
    const benefitType =
      step.standardBenefit?.type ?? step.optionalBenefits[0]?.type;
    const benefitTypeName = getBenefitTypeName(
      benefitType !== 'ACTIVITY'
        ? benefitType
        : (step.standardBenefit?.activity?.activityType ??
            step.optionalBenefits[0].activity?.activityType ??
            ''),
    );
    switch (benefitTypeName) {
      case BenefitType.MEAL:
        if (circuitDaySteps.length <= indexMeal)
          circuitDaySteps.push(createCircuitDay().steps[0]);
        circuitDaySteps[indexMeal].meals = parseSingleStep(
          step,
          translationMap,
          t,
        );
        indexMeal++;
        break;
      case BenefitType.HOTEL:
        if (circuitDaySteps.length <= indexAccommodation)
          circuitDaySteps.push(createCircuitDay().steps[0]);
        circuitDaySteps[indexAccommodation].accommodations = parseSingleStep(
          step,
          translationMap,
          t,
        );
        indexAccommodation++;
        break;
      case BenefitType.VEHICLE:
      case BenefitType.VEHICLE_WITH_CHAUFFEUR:
      case BenefitType.VOL:
      case BenefitType.TRANSFER:
        if (circuitDaySteps.length <= indexTransport)
          circuitDaySteps.push(createCircuitDay().steps[0]);
        circuitDaySteps[indexTransport].transport = parseSingleStep(
          step,
          translationMap,
          t,
        );
        indexTransport++;
        break;
      case BenefitType.ACTIVITY:
        if (benefitType) {
          if (circuitDaySteps.length <= indexActivity)
            circuitDaySteps.push(createCircuitDay().steps[0]);
          circuitDaySteps[indexActivity].activities = parseSingleStep(
            step,
            translationMap,
            t,
          );
          indexActivity++;
        }
        break;
    }
  });

  return circuitDaySteps;
};

const parseSingleStep = (
  step: CircuitResponse['circuitDays'][number]['CircuitDayStep'][number],
  translationMap: BenefitTranslationMap | undefined,
  t: Translator,
) =>
  ({
    ...step,
    id: step.id,
    standardBenefit: step.standardBenefit
      ? parseSingleBenefit(step.standardBenefit, translationMap)
      : undefined,
    optionalBenefits: (step.optionalBenefits ?? []).map((benefit) =>
      parseSingleBenefit(benefit, translationMap),
    ),
    standardBenefitPensionType:
      step.accommodationSelections?.find(
        (selection) => selection.selectionType === 'STANDARD',
      )?.pensionType ??
      step.standardBenefit?.hotel?.pricingPensionType ??
      undefined,
    optionalBenefitsPensionTypes: Object.fromEntries(
      (step.accommodationSelections ?? [])
        .filter((selection) => selection.selectionType === 'OPTION')
        .map((selection) => [selection.benefitId, selection.pensionType]),
    ),
    standardDisplays: parseStepDisplays(step.standardBenefitDisplays ?? []),
    optionalDisplays: parseStepDisplays(step.optionalBenefitsDisplays ?? []),
    nbDays: step.nbDays ? Number(step.nbDays) : 1,
    time: step.time
      ? CIRCUIT_DAY_PERIODS(t).find((period) => period.value === step.time)
      : undefined,
  }) as CircuitFormData['circuitDays'][number]['steps'][number]['activities'];

const parseSingleBenefit = (
  benefit: CircuitResponse['circuitDays'][number]['CircuitDayStep'][number]['optionalBenefits'][number],
  translationMap: BenefitTranslationMap | undefined,
) => {
  const translations =
    translationMap?.get(benefit.id) ||
    ({} as Record<SupportedLocale, BenefitTranslation>);

  return {
    id: benefit.id,
    name: benefit.name,
    type: benefit.type,
    currency: benefit.currency,
    marginRate: numberIfNotUndefined(benefit.marginRate),
    supplier: {
      marginRate: numberIfNotUndefined(benefit.supplier.marginRate),
    },
    Pricing:
      benefit.Pricing && benefit.Pricing.length
        ? benefit.Pricing.map((pricing) => ({
            name: pricing.name ?? undefined,
            periods:
              pricing.periods && pricing.periods.length
                ? pricing.periods.map((period) => ({
                    startDate: period.startDate
                      ? new Date(period.startDate)
                      : undefined,
                    endDate: period.endDate
                      ? new Date(period.endDate)
                      : undefined,
                    applicationDays: period.applicationDays ?? [],
                  }))
                : [],
            pricePartitions:
              pricing.pricePartitions && pricing.pricePartitions.length
                ? pricing.pricePartitions.map((partition) => ({
                    from:
                      benefit.type === 'VOL'
                        ? 1
                        : partition.from
                          ? Number(partition.from)
                          : undefined,
                    to:
                      benefit.type === 'VOL'
                        ? 1
                        : partition.to
                          ? Number(partition.to)
                          : undefined,
                    price: numberIfNotUndefined(partition.price),
                    specialPrice: numberIfNotUndefined(partition.specialPrice),
                  }))
                : [],
          }))
        : [],
    activity: benefit.activity
      ? {
          ...benefit.activity,
          activityType: benefit.activity.activityType ?? undefined,
          pricingType: benefit.activity.pricingType ?? undefined,
        }
      : undefined,
    VehicleWithChauffeur: benefit.VehicleWithChauffeur
      ? {
          ...benefit.VehicleWithChauffeur,
          maxCapacity: benefit.VehicleWithChauffeur.maxCapacity
            ? Number(benefit.VehicleWithChauffeur.maxCapacity)
            : undefined,
        }
      : undefined,
    flight: benefit.flight,
    carRental: benefit.carRental
      ? {
          ...benefit.carRental,
          carCategories:
            benefit.carRental.carCategories &&
            benefit.carRental.carCategories.length
              ? benefit.carRental.carCategories.map((category) => ({
                  ...category,
                  insurances: category.insurances
                    ? category.insurances
                    : undefined,
                  options: category.options ? category.options : undefined,
                  taxes: category.taxes ? category.taxes : undefined,
                  oneWayFees: category.oneWayFees
                    ? category.oneWayFees
                    : undefined,
                  maxCapacity: category.maxCapacity
                    ? Number(category.maxCapacity)
                    : undefined,
                  pricings:
                    category.pricings && category.pricings.length
                      ? category.pricings.map((pricing) => ({
                          name: pricing.name ?? undefined,
                          periods:
                            pricing.periods && pricing.periods.length
                              ? pricing.periods.map((period) => ({
                                  startDate: period.startDate
                                    ? new Date(period.startDate)
                                    : undefined,
                                  endDate: period.endDate
                                    ? new Date(period.endDate)
                                    : undefined,
                                  applicationDays: period.applicationDays ?? [],
                                }))
                              : [],
                          pricePartitions:
                            pricing.pricePartitions &&
                            pricing.pricePartitions.length
                              ? pricing.pricePartitions.map((partition) => ({
                                  from: partition.from
                                    ? Number(partition.from)
                                    : undefined,
                                  to: partition.to
                                    ? Number(partition.to)
                                    : undefined,
                                  price: numberIfNotUndefined(partition.price),
                                }))
                              : [],
                        }))
                      : undefined,
                }))
              : [],
        }
      : undefined,
    hotel: benefit.hotel
      ? {
          ...benefit.hotel,
          pricingType: benefit.hotel.pricingType ?? undefined,
          reducedPriceExists: benefit.hotel.reducedPriceExists ?? undefined,
          isReduction: benefit.hotel.isReduction ?? undefined,
          HotelRoomCategory:
            benefit.hotel.HotelRoomCategory &&
            benefit.hotel.HotelRoomCategory.length
              ? benefit.hotel.HotelRoomCategory.map((category) => ({
                  ...category,
                  pricings:
                    category.pricings && category.pricings.length
                      ? category.pricings.map((pricing) => ({
                          ...pricing,
                          periods:
                            pricing.periods && pricing.periods.length
                              ? pricing.periods.map((period) => ({
                                  startDate: period.startDate
                                    ? new Date(period.startDate)
                                    : undefined,
                                  endDate: period.endDate
                                    ? new Date(period.endDate)
                                    : undefined,
                                  applicationDays: period.applicationDays ?? [],
                                }))
                              : [],
                        }))
                      : [],
                }))
              : [],
          associatedBenefits:
            benefit.hotel.associatedBenefits &&
            benefit.hotel.associatedBenefits.length > 0
              ? benefit.hotel.associatedBenefits.map((associatedBenefit) => ({
                  id: associatedBenefit.id,
                  benefitId: associatedBenefit.benefitId,
                  frequency: associatedBenefit.frequency,
                  benefit: associatedBenefit.benefit
                    ? {
                        id: associatedBenefit.benefit.id,
                        name: associatedBenefit.benefit.name,
                        code: associatedBenefit.benefit.code,
                        type: associatedBenefit.benefit.type,
                        country: associatedBenefit.benefit.country,
                        supplier: {
                          marginRate: numberIfNotUndefined(
                            associatedBenefit.benefit.supplier?.marginRate,
                          ),
                          name:
                            associatedBenefit.benefit.supplier?.name ??
                            undefined,
                          id:
                            associatedBenefit.benefit.supplier?.id ?? undefined,
                        },
                        Pricing:
                          associatedBenefit.benefit.Pricing &&
                          associatedBenefit.benefit.Pricing.length
                            ? associatedBenefit.benefit.Pricing.map(
                                (pricing) => ({
                                  name: pricing.name ?? undefined,
                                  periods:
                                    pricing.periods && pricing.periods.length
                                      ? pricing.periods.map((period) => ({
                                          startDate: period.startDate
                                            ? new Date(period.startDate)
                                            : undefined,
                                          endDate: period.endDate
                                            ? new Date(period.endDate)
                                            : undefined,
                                          applicationDays:
                                            period.applicationDays ?? [],
                                        }))
                                      : [],
                                  pricePartitions:
                                    pricing.pricePartitions &&
                                    pricing.pricePartitions.length
                                      ? pricing.pricePartitions.map(
                                          (partition) => ({
                                            from:
                                              associatedBenefit.benefit
                                                ?.type === 'VOL'
                                                ? 1
                                                : partition.from
                                                  ? Number(partition.from)
                                                  : undefined,
                                            to:
                                              associatedBenefit.benefit
                                                ?.type === 'VOL'
                                                ? 1
                                                : partition.to
                                                  ? Number(partition.to)
                                                  : undefined,
                                            price: numberIfNotUndefined(
                                              partition.price,
                                            ),
                                            specialPrice: numberIfNotUndefined(
                                              partition.specialPrice,
                                            ),
                                          }),
                                        )
                                      : [],
                                }),
                              )
                            : undefined,
                        activity: associatedBenefit.benefit.activity,
                      }
                    : undefined,
                }))
              : [],
        }
      : undefined,
    translations,
  } as CircuitFormData['circuitDays'][number]['steps'][number]['activities']['optionalBenefits'][number];
};

const parseStepDisplays = (
  displays: CircuitResponse['circuitDays'][number]['CircuitDayStep'][number]['standardBenefitDisplays'],
) =>
  displays.map((display) => {
    switch (display) {
      case 'NAME':
        return 'name';
      case 'SHORT_DESCRIPTION':
        return 'shortDescription';
      case 'LONG_DESCRIPTION':
        return 'longDescription';
    }
  });

export const addMultiDaySteps = <
  T extends {
    day: CircuitResponse['circuitDays'][number]['day'];
    CircuitDayStep: CircuitResponse['circuitDays'][number]['CircuitDayStep'];
  },
>(
  circuitDays: T[],
) => {
  const processedMultiDaySteps = new Set<string>();

  const sortedCircuitDays = circuitDays.toSorted((a, b) => a.day - b.day);

  const daysWithMultiDaySteps = sortedCircuitDays.map(
    (circuitDay, indexDay) => ({
      ...circuitDay,
      CircuitDayStep: circuitDay.CircuitDayStep.map((step) => ({
        ...step,
        indexFirstDay: indexDay,
      })),
    }),
  );

  sortedCircuitDays.forEach((circuitDay, indexDay) => {
    const stepsWithMultiDays = circuitDay.CircuitDayStep.filter(
      (step) =>
        step.nbDays && step.nbDays > 1 && !processedMultiDaySteps.has(step.id),
    );
    stepsWithMultiDays.forEach((step) => {
      for (let i = 1; i < (step.nbDays ?? 1); i++) {
        if (indexDay + i < daysWithMultiDaySteps.length) {
          processedMultiDaySteps.add(step.id);
          daysWithMultiDaySteps[indexDay + i].CircuitDayStep.push({
            ...step,
            indexFirstDay: indexDay,
          });
        }
      }
    });
  });

  return daysWithMultiDaySteps;
};
